/**
 * Central Operational Truth Registry for AI Bridge Hub Fusion.
 * Categorizes interface capabilities according to their true operational status.
 * Categories: REAL | DEMO | STATIC | INCOMPLETE | PLANNED
 */

export const OPERATIONAL_STATUS = {
  REAL: 'REAL',
  DEMO: 'DEMO',
  STATIC: 'STATIC',
  INCOMPLETE: 'INCOMPLETE',
  PLANNED: 'PLANNED'
};

export const OPERATIONAL_TRUTH = {
  sessions: {
    id: 'sessions',
    status: OPERATIONAL_STATUS.REAL,
    label: 'Real Sessions',
    description: 'MongoDB-backed session creation, listing, and state persistence',
    evidenceSource: 'FastAPI /api/sessions endpoint & MongoDB'
  },
  messages: {
    id: 'messages',
    status: OPERATIONAL_STATUS.REAL,
    label: 'Real Messages',
    description: 'MongoDB-backed message history between Origin AI, Production AI, and User',
    evidenceSource: 'FastAPI /api/messages endpoint & MongoDB'
  },
  projects: {
    id: 'projects',
    status: OPERATIONAL_STATUS.REAL,
    label: 'Real Projects',
    description: 'MongoDB-backed project state and artifact list management',
    evidenceSource: 'FastAPI /api/projects endpoint & MongoDB'
  },
  humanApproval: {
    id: 'humanApproval',
    status: OPERATIONAL_STATUS.REAL,
    label: 'Human Approval Flow',
    description: 'Explicit human confirmation required before updating Production AI connector',
    evidenceSource: 'FastAPI /api/sessions/{id}/confirm-replacement endpoint'
  },
  openAIKeyVerification: {
    id: 'openAIKeyVerification',
    status: OPERATIONAL_STATUS.REAL,
    label: 'OpenAI Key Verification',
    description: 'Live HTTPS GET /v1/models validation using provided OpenAI API key',
    evidenceSource: 'FastAPI connector registry and OpenAI adapter'
  },
  originAIWorkflow: {
    id: 'originAIWorkflow',
    status: OPERATIONAL_STATUS.DEMO,
    label: 'Origin AI Demo Connector',
    description: 'Simulated interactive architect agent in Demo mode',
    evidenceSource: 'FastAPI mock connector & local state'
  },
  productionAIWorkflow: {
    id: 'productionAIWorkflow',
    status: OPERATIONAL_STATUS.DEMO,
    label: 'Production AI Demo Roster',
    description: 'Simulated multi-agent production roster cycling across project phases',
    evidenceSource: 'FastAPI _REPLACEMENT_ROSTER demo mock'
  },
  guidedDemo: {
    id: 'guidedDemo',
    status: OPERATIONAL_STATUS.DEMO,
    label: 'Guided Demo Initialization',
    description: 'Preconfigured demo session with sample artifacts and message history',
    evidenceSource: 'FastAPI /api/demo/initialize endpoint'
  },
  sandbox: {
    id: 'sandbox',
    status: OPERATIONAL_STATUS.REAL,
    label: 'Isolated Test Sandbox',
    description: 'Isolated real container execution without network access, with structured argv and verified exit code telemetry',
    evidenceSource: 'FastAPI /api/sandbox/{project_id}/run and /api/sandbox/executions/{execution_id}'
  },
  deployment: {
    id: 'deployment',
    status: OPERATIONAL_STATUS.DEMO,
    label: 'Simulated Deployment',
    description: 'Simulated deployment pipeline generating mock preview URLs',
    evidenceSource: 'FastAPI /api/projects/{id}/deploy mock'
  },
  archiveDownload: {
    id: 'archiveDownload',
    status: OPERATIONAL_STATUS.REAL,
    label: 'REAL / EXPORTABLE',
    description: 'In-memory ZIP archive generation & streaming download containing manifest.json, project.json, and files',
    evidenceSource: 'FastAPI validated in-memory ZIP export through POST /archive and GET /download'
  },
  terminal: {
    id: 'terminal',
    status: OPERATIONAL_STATUS.STATIC,
    label: 'Operations Activity Console',
    description: 'Purely local React event log viewer without OS terminal access',
    evidenceSource: 'Local React State (TerminalDrawer.jsx)'
  },
  missions: {
    id: 'missions',
    status: OPERATIONAL_STATUS.STATIC,
    label: 'Sample Missions Drawer',
    description: 'Static sample mission cards and local UI chat interface',
    evidenceSource: 'Local React State (MissionsChatSidebar.jsx)'
  },
  controlChat: {
    id: 'controlChat',
    status: OPERATIONAL_STATUS.STATIC,
    label: 'Control Chat',
    description: 'Local UI message dispatcher without background agent protocol',
    evidenceSource: 'Local React Component'
  },
  connectionFilters: {
    id: 'connectionFilters',
    status: OPERATIONAL_STATUS.STATIC,
    label: 'Connection Filters',
    description: 'Local UI filter controls for visual drawer categories',
    evidenceSource: 'Local React Component State'
  },
  telemetry: {
    id: 'telemetry',
    status: OPERATIONAL_STATUS.STATIC,
    label: 'Telemetry UI Toggle',
    description: 'Visual UI toggle without backend metrics collection or telemetry stream',
    evidenceSource: 'Local React Component State'
  },
  contextualMemory: {
    id: 'contextualMemory',
    status: OPERATIONAL_STATUS.STATIC,
    label: 'Contextual Memory Toggle',
    description: 'Visual UI toggle without vector memory backend storage',
    evidenceSource: 'Local React Component State'
  },
  contextLayers: {
    id: 'contextLayers',
    status: OPERATIONAL_STATUS.STATIC,
    label: 'Context Layers Visual Canvas',
    description: 'Interactive visual canvas of 4 context bands without SystemAtlas or Orion persistence',
    evidenceSource: 'Local React Component State (ContextLayersWorkspace.jsx)'
  },
  orchestrator: {
    id: 'orchestrator',
    status: OPERATIONAL_STATUS.STATIC,
    label: 'Orchestrator UI Prototype',
    description: 'Visual header status bar and toast triggers without background orchestrator engine',
    evidenceSource: 'Local React Component (OrchestratorHeaderBar.jsx)'
  },
  anthropic: {
    id: 'anthropic',
    status: OPERATIONAL_STATUS.PLANNED,
    label: 'Anthropic Claude Connector',
    description: 'Planned API adapter returning HTTP 501 Not Implemented',
    evidenceSource: 'Backend HTTP 501 stub'
  },
  gemini: {
    id: 'gemini',
    status: OPERATIONAL_STATUS.PLANNED,
    label: 'Google Gemini Connector',
    description: 'Planned API adapter returning HTTP 501 Not Implemented',
    evidenceSource: 'Backend HTTP 501 stub'
  },
  mcp: {
    id: 'mcp',
    status: OPERATIONAL_STATUS.PLANNED,
    label: 'Model Context Protocol (MCP)',
    description: 'Planned MCP server integration returning HTTP 501 Not Implemented',
    evidenceSource: 'Backend HTTP 501 stub'
  },
  a2a: {
    id: 'a2a',
    status: OPERATIONAL_STATUS.PLANNED,
    label: 'Agent-to-Agent (A2A)',
    description: 'Planned inter-agent communication protocol returning HTTP 501 Not Implemented',
    evidenceSource: 'Backend HTTP 501 stub'
  },
  localAgent: {
    id: 'localAgent',
    status: OPERATIONAL_STATUS.PLANNED,
    label: 'Local Agent Engine',
    description: 'Planned local model runner returning HTTP 501 Not Implemented',
    evidenceSource: 'Backend HTTP 501 stub'
  },
  nvidia: {
    id: 'nvidia',
    status: OPERATIONAL_STATUS.PLANNED,
    label: 'NVIDIA NIM Connector',
    description: 'Planned API adapter returning HTTP 501 Not Implemented',
    evidenceSource: 'Backend HTTP 501 stub'
  }
};
